import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  AutoAwesome as AiIcon,
  Send as SendIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useWebLLM } from "../../../contexts/WebLLMContext";
import { apiGetContactsCollection } from "../../../graphQL/addressbook/collections/contacts";
import { contactMatchesFilter } from "../../../utils/campaignContactFilter";

/*
 * CampaignCriteriaChat
 * --------------------
 * Conversational builder for a campaign's `data.contactFilter`. The user types
 * plain language ("brokers tagged multifamily in Sacramento") and the local
 * WebLLM model maps it to the EXACT filter schema below — nothing more.
 *
 * Authoritative contactFilter schema (see CampaignDetailPanel.getFilter):
 *   query      : string           free-text search (name / company / email / notes)
 *   categories : string[]         one or more contact types / categories
 *   status   : string[]         one or more contact statuses
 *   mode     : "AND" | "OR"     how the criteria combine
 *   cities   : string[]         city names
 *   zipcodes : string[]         5-digit zip codes
 *
 * `domain` is NEVER part of the filter — the campaign is always scoped to its
 * own domain. Anything the user asks for that is not one of the six fields
 * above is surfaced as "unsupported" and NOT invented as a new field.
 */

const FILTER_FIELDS = ["query", "categories", "status", "mode", "cities", "zipcodes"];

const SYSTEM_PROMPT = `You translate a real-estate marketer's plain-language description of a campaign audience into a STRICT JSON filter. You NEVER invent fields.

The ONLY allowed output fields are exactly these six:
- "query": string. Free text to match on a contact's name, company, email, or notes. Use for keywords, tags, specialties, industries, or any descriptor that is not a city/zip/status/category.
- "categories": array of strings. One or more contact types / categories (e.g. "Broker", "Lender", "Investor"). Empty array if none.
- "status": array of strings. One or more contact lifecycle statuses (e.g. "active", "lead", "prospect", "client"). Empty array if none.
- "mode": "AND" or "OR". "AND" = a contact must match ALL provided criteria (default). "OR" = match ANY.
- "cities": array of strings. City names only.
- "zipcodes": array of strings. 5-digit US zip codes only.

Rules:
1. Respond with a SHORT one-sentence plain-English confirmation, then a fenced JSON code block. Nothing else.
2. The JSON MUST contain all six fields plus an "unsupported" array.
3. "unsupported": list every piece of the request you could NOT express with the six fields (for example: "not emailed in the last 90 days", "opened previous campaign", "company size", "revenue over $1M"). There is NO field for engagement history, dates, counts, or activity. Put those in "unsupported" and do NOT force them into query.
4. Never place a city, zip, status, or category into "query". Never place free-text keywords into "categories".
5. Keep prior turns in mind: refine the SAME filter, returning the full updated object each time.
6. Output English only. Do not add commentary after the JSON block.
7. IGNORE any mention of a domain, account, tenant, workspace, or team. The audience is already scoped to the user's own domain automatically. Never add such a word to any field (not even "query") and do not list it as unsupported.

Example user: "brokers tagged multifamily in Sacramento and Elk Grove, active leads"
Example response:
Targeting active-or-lead brokers tagged multifamily in Sacramento and Elk Grove.
\`\`\`json
{"query":"multifamily","categories":["Broker"],"status":["active","lead"],"mode":"AND","cities":["Sacramento","Elk Grove"],"zipcodes":[],"unsupported":[]}
\`\`\`

Example user: "lenders in 95814 who haven't been emailed in 90 days"
Example response:
Targeting lenders in 95814. I cannot express the emailing history.
\`\`\`json
{"query":"","category":"Lender","status":[],"mode":"AND","cities":[],"zipcodes":["95814"],"unsupported":["not emailed in the last 90 days"]}
\`\`\`

Example user: "investors in Elk Grove or Roseville, match any of them"
Example response:
Targeting investors in Elk Grove or Roseville, matching any city.
\`\`\`json
{"query":"","categories":["Investor"],"status":[],"mode":"OR","cities":["Elk Grove","Roseville"],"zipcodes":[],"unsupported":[]}
\`\`\`

Example user: "everyone" or "all contacts"
Example response:
Targeting every contact, no narrowing criteria.
\`\`\`json
{"query":"","categories":[],"status":[],"mode":"AND","cities":[],"zipcodes":[],"unsupported":[]}
\`\`\`

Reminder: output ONLY the six fields plus "unsupported". Never add any other key. Never rename a field.`;

const asString = (v) => String(v == null ? "" : v).trim();
const asStringList = (v) => {
  if (Array.isArray(v)) return Array.from(new Set(v.map(asString).filter(Boolean)));
  return Array.from(
    new Set(
      asString(v)
        .split(/[,\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
};
const asZipList = (v) =>
  Array.from(
    new Set(
      asStringList(v)
        .map((z) => z.replace(/[^\d-]/g, "").split("-")[0].slice(0, 5))
        .filter((z) => /^\d{5}$/.test(z)),
    ),
  );

// Normalize whatever the model emits down to the strict schema. Unknown keys are
// dropped; only the six real fields survive (plus the unsupported note list).
const normalizeFilter = (raw) => {
  const obj = raw && typeof raw === "object" ? raw : {};
  return {
    query: asString(obj.query),
    categories: asStringList(obj.categories ?? obj.category),
    status: asStringList(obj.status),
    mode: asString(obj.mode).toUpperCase() === "OR" ? "OR" : "AND",
    cities: asStringList(obj.cities),
    zipcodes: asZipList(obj.zipcodes),
    unsupported: asStringList(obj.unsupported),
  };
};

const extractJson = (text) => {
  if (!text) return null;
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = fenced ? fenced[1] : (text.match(/\{[\s\S]*\}/) || [])[0];
  if (!candidate) return null;
  try {
    return JSON.parse(candidate.trim());
  } catch {
    return null;
  }
};

const stripJson = (text) =>
  String(text || "")
    .replace(/```(?:json)?\s*[\s\S]*?```/gi, "")
    .replace(/\{[\s\S]*\}/, "")
    .trim();

export const isEmptyFilter = (f) =>
  !f.query &&
  asStringList(f.categories ?? f.category).length === 0 &&
  f.status.length === 0 &&
  f.cities.length === 0 &&
  f.zipcodes.length === 0;

export const summarizeFilter = (f) => {
  const parts = [];
  if (f.query) parts.push(`text "${f.query}"`);
  const categories = asStringList(f.categories ?? f.category);
  if (categories.length) parts.push(`category ${categories.join(" / ")}`)
  if (f.status.length) parts.push(`status ${f.status.join(" / ")}`);
  if (f.cities.length) parts.push(`cities ${f.cities.join(", ")}`);
  if (f.zipcodes.length) parts.push(`zips ${f.zipcodes.join(", ")}`);
  if (!parts.length) return "Everyone in this domain (no narrowing criteria).";
  const joiner = f.mode === "OR" ? " OR " : " AND ";
  return parts.join(joiner);
};

/*
 * `inline` renders the chat as a plain card body instead of a dialog: the
 * raw filter JSON, the field chips, and the Cancel button are hidden so a
 * non-technical user only sees the plain-English audience and the count.
 */
const CampaignCriteriaChat = ({ domain, authorId, initialFilter, onApply, onClose, inline = false, applied = false }) => {
  const { engine, ready, loading, downloading, progress, error, init } = useWebLLM();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [derived, setDerived] = useState(() => normalizeFilter(initialFilter));
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const listRef = useRef(null);
  const abortRef = useRef(false);

  useEffect(() => {
    if (!ready && !loading && !error) init();
  }, [ready, loading, error, init]);

  // The campaign list is loaded without `data`, so the campaign's saved filter
  // can arrive after this chat mounted; re-seed the derived filter when it does.
  const initialFilterKey = JSON.stringify(initialFilter || {});
  useEffect(() => {
    setDerived(normalizeFilter(initialFilter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilterKey]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const hasUnsupported = derived.unsupported.length > 0;

  // Live recipient count for the currently derived filter. Mirrors the
  // CampaignContactsPanel resolution: server-side for simple filters, local
  // match when cities / zips / OR mode need client evaluation.
  const citiesKey = derived.cities.join(",");
  const zipKey = derived.zipcodes.join(",");
  const statusKey = derived.status.join(",");
  const needsLocal =
    derived.mode === "OR" || derived.cities.length > 0 || derived.zipcodes.length > 0;

  useEffect(() => {
    if (!domain) {
      setCount(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setCounting(true);
      try {
        const filters = { domain };
        if (!needsLocal) {
          if (derived.query) filters.text = derived.query;
          if (derived.categories.length === 1) filters.category = derived.categories[0];
          if (derived.categories.length > 1) filters.categories = derived.categories;
          if (derived.status.length === 1) filters.status = derived.status[0];
          if (derived.status.length > 1) filters.statuses = derived.status;
        }
        const result = await apiGetContactsCollection({
          ...(authorId ? { authorId } : {}),
          filters,
          pagination: { page: 1, limit: needsLocal ? 1000 : 1 },
          ordering: { field: "updatedAt", order: "Desc" },
        });
        if (cancelled) return;
        if (needsLocal) {
          const items = Array.isArray(result?.items) ? result.items : [];
          setCount(items.filter((c) => contactMatchesFilter(c, derived)).length);
        } else {
          setCount(Number(result?.total) || 0);
        }
      } catch (err) {
        console.error("Recipient count failed:", err);
        if (!cancelled) setCount(null);
      } finally {
        if (!cancelled) setCounting(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, authorId, derived.query, derived.categories.join(","), statusKey, derived.mode, citiesKey, zipKey, needsLocal]);

  const send = useCallback(
    async (text) => {
      const q = asString(text ?? input);
      if (!q || !engine || !ready || generating) return;
      const next = [...messages, { role: "user", content: q }];
      setMessages(next);
      setInput("");
      setGenerating(true);
      abortRef.current = false;
      try {
        const chunks = await engine.chat.completions.create({
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            ...next.map((m) => ({ role: m.role, content: m.content })),
          ],
          temperature: 0.1,
          top_p: 0.8,
          max_tokens: 400,
          stream: true,
        });
        let acc = "";
        setMessages((prev) => [...prev, { role: "assistant", content: "", streaming: true }]);
        for await (const chunk of chunks) {
          if (abortRef.current) break;
          const delta = chunk.choices?.[0]?.delta?.content || "";
          if (delta) {
            acc += delta;
            const current = acc;
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: current, streaming: true };
              return copy;
            });
          }
        }
        const parsed = extractJson(acc);
        let display = stripJson(acc) || "Here is the derived filter.";
        if (parsed) {
          const norm = normalizeFilter(parsed);
          setDerived(norm);
          if (norm.unsupported.length) {
            display +=
              `\n\nI cannot express: ${norm.unsupported.join("; ")}. ` +
              "There is no filter field for that. Please rephrase using contact type, status, city, zip, or keywords.";
          }
        } else {
          display +=
            "\n\nI could not derive a structured filter from that. Try naming a contact type, status, city, zip, or keyword.";
        }
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: display, streaming: false };
          return copy;
        });
      } catch (err) {
        console.error("Criteria chat failed:", err);
        setMessages((prev) => {
          const copy = [...prev.filter((m) => !m.streaming)];
          copy.push({ role: "assistant", content: "Something went wrong deriving the filter. Please try again." });
          return copy;
        });
      } finally {
        setGenerating(false);
      }
    },
    [input, engine, ready, generating, messages],
  );

  const statusLine = useMemo(() => {
    if (error) return "The local AI model failed to load (needs Chrome or Edge with WebGPU).";
    if (downloading) return `Downloading the AI model (one time)... ${Math.round((progress || 0) * 100)}%`;
    if (loading) return "Loading the AI model...";
    if (!ready) return "Preparing the AI model...";
    return null;
  }, [error, downloading, loading, progress, ready]);

  const applyFilter = () => {
    // Strip the audit-only "unsupported" note before persisting; only the six
    // real schema fields ever reach data.contactFilter.
    const clean = {};
    FILTER_FIELDS.forEach((k) => {
      clean[k] = derived[k];
    });
    onApply?.(clean);
  };

  const suggestions = [
    "active brokers in Sacramento",
    "lenders in 95814",
    "investors in Elk Grove or Roseville, match any",
    "brokers tagged multifamily",
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: inline ? 380 : 460, minHeight: 0 }}>
      {/* Derived filter + live count */}
      <Box sx={{ p: 1.25, backgroundColor: "#F4F5FB", borderBottom: "1px solid #E8EAF6" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#283593", textTransform: "uppercase", letterSpacing: 0.4 }}>
            {inline ? "Audience" : "Derived filter"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {counting ? (
              <CircularProgress size={12} sx={{ color: "#3F51B5" }} />
            ) : (
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1A237E" }}>
                {!domain ? "—" : count == null ? "—" : `${count} recipients`}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12, color: "#1A1F36", mb: 0.75 }}>{summarizeFilter(derived)}</Typography>
        {inline ? null : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
          <Chip size="small" label={`mode: ${derived.mode}`} sx={{ backgroundColor: "#E8EAF6", color: "#283593", fontWeight: 700 }} />
          {derived.query ? <Chip size="small" label={`query: ${derived.query}`} sx={{ backgroundColor: "#E8EAF6", color: "#283593" }} /> : null}
          {derived.categories.map((c) => (
            <Chip key={c} size="small" label={`category: ${c}`} sx={{ backgroundColor: "#E8EAF6", color: "#283593" }} />
          ))}
          {derived.status.map((s) => (
            <Chip key={`st-${s}`} size="small" label={`status: ${s}`} sx={{ backgroundColor: "#E8EAF6", color: "#283593" }} />
          ))}
          {derived.cities.map((c) => (
            <Chip key={`ci-${c}`} size="small" label={c} sx={{ backgroundColor: "#DBEAFE", color: "#1D4ED8", fontWeight: 700 }} />
          ))}
          {derived.zipcodes.map((z) => (
            <Chip key={`zi-${z}`} size="small" label={z} sx={{ backgroundColor: "#DBEAFE", color: "#1D4ED8", fontWeight: 700 }} />
          ))}
        </Box>
        )}
        {hasUnsupported ? (
          <Typography sx={{ fontSize: 11, color: "#B45309", fontWeight: 600 }}>
            Cannot express: {derived.unsupported.join("; ")}. Rephrase using type, status, city, zip, or keywords.
          </Typography>
        ) : null}
        {inline ? null : (
        <Box sx={{ mt: 0.5, p: 0.75, borderRadius: 1, backgroundColor: "#FFFFFF", border: "1px solid #E8EAF6" }}>
          <Typography sx={{ fontSize: 10.5, fontFamily: "monospace", color: "#283593", wordBreak: "break-word" }}>
            {JSON.stringify(
              FILTER_FIELDS.reduce((acc, k) => ({ ...acc, [k]: derived[k] }), {}),
            )}
          </Typography>
        </Box>
        )}
      </Box>

      {/* Messages */}
      <Box ref={listRef} sx={{ flex: 1, overflowY: "auto", p: 1.25, backgroundColor: "#FFFFFF" }}>
        {messages.length === 0 ? (
          <Box>
            <Typography sx={{ fontSize: 12.5, color: "#1A1F36", mb: 1 }}>
              {inline
                ? "Type who should get this email, in plain words:"
                : "Describe who should receive this campaign, then Apply. Tap an example to start:"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {suggestions.map((s) => (
                <Box
                  key={s}
                  onClick={() => setInput(s)}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    fontSize: 12.5,
                    backgroundColor: "#F4F5FB",
                    border: "1px solid #E8EAF6",
                    cursor: "pointer",
                    color: "#283593",
                    "&:hover": { borderColor: "#3F51B5", backgroundColor: "#E8EAF6" },
                  }}
                >
                  {s}
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "88%",
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  fontSize: 12.5,
                  whiteSpace: "pre-wrap",
                  backgroundColor: m.role === "user" ? "#3F51B5" : "#F4F5FB",
                  color: m.role === "user" ? "#FFFFFF" : "#1A1F36",
                  border: m.role === "user" ? "none" : "1px solid #E8EAF6",
                }}
              >
                {m.content || (m.streaming ? "…" : "")}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Status + input */}
      {statusLine ? (
        <Typography sx={{ px: 1.25, py: 0.5, fontSize: 11, color: error ? "#C62828" : "#5C6BC0", backgroundColor: "#F4F5FB" }}>
          {statusLine}
        </Typography>
      ) : null}
      {messages.length > 0 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, px: 1, pt: 1 }}>
          <Typography sx={{ fontSize: 10.5, color: "#5C6BC0", fontWeight: 600, alignSelf: "center", mr: 0.25 }}>
            Refine:
          </Typography>
          {suggestions.map((s) => (
            <Chip
              key={`ex-${s}`}
              size="small"
              label={s}
              onClick={() => setInput(s)}
              sx={{
                backgroundColor: "#F4F5FB",
                border: "1px solid #E8EAF6",
                color: "#283593",
                fontSize: 11,
                cursor: "pointer",
                "&:hover": { backgroundColor: "#E8EAF6" },
              }}
            />
          ))}
        </Box>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, p: 1, borderTop: "1px solid #E8EAF6" }}>
        <TextField
          placeholder={ready ? "Describe the audience..." : "Loading model..."}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={!ready || generating}
          fullWidth
          multiline
          maxRows={3}
          size="small"
        />
        <IconButton
          onClick={() => send()}
          disabled={!ready || generating || !input.trim()}
          aria-label="Send"
          sx={{ color: "#3F51B5", "&:hover": { color: "#3949AB" } }}
        >
          {generating ? <CircularProgress size={18} sx={{ color: "#3F51B5" }} /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: inline ? "flex-end" : "space-between", gap: 1, p: 1, borderTop: "1px solid #E8EAF6", backgroundColor: "#F4F5FB" }}>
        {inline ? null : (
        <Button onClick={onClose} size="small" sx={{ color: "#3949AB", textTransform: "none" }}>
          Cancel
        </Button>
        )}
        <Button
          onClick={applyFilter}
          size="small"
          variant="contained"
          startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
          disabled={isEmptyFilter(derived)}
          sx={{
            textTransform: "none",
            backgroundColor: "#3F51B5",
            "&:hover": { backgroundColor: "#3949AB" },
          }}
        >
          {inline ? (applied ? "Audience set" : "Use this audience") : "Apply to campaign"}
        </Button>
      </Box>
    </Box>
  );
};

export { AiIcon as CriteriaChatIcon };
export default CampaignCriteriaChat;
